import React from "react";
import cn from "clsx";
import { css } from "linaria";
import distanceInWords from "date-fns/distance_in_words";
import {
  Menu,
  MenuList,
  MenuButton,
  MenuItem,
  MenuLink,
} from "@reach/menu-button";
import "@reach/menu-button/styles.css";

import DotIcon from "../../Icons/Dot";
import AppLink from "../../AppLink";
import NotificationIcon from "../../Icons/Notification";
import { notificationsViewed } from "../../../contexts/Notifications/actions";
import {
  useNotificationsState,
  useNotificationsDispatch,
} from "../../../contexts/Notifications/context";
import colors from "../../../utils/colorConstants";
import useModalToggleTracking from "../../../hooks/useModalToggleTracking";

const NotificationClasses = "w-full border-alto cursor-pointer";

const ReachMenuListOverride = css`
  @apply text-lapel bg-white divide-y overflow-x-hidden max-h-80 max-w-sm border-dove;
  width: 20rem;
  max-width: 20rem;

  &[data-reach-menu-list] {
    @apply text-xs border-dove;
    padding: 0.5rem;

    & > div {
      @apply border-alto;
    }
  }

  & [data-reach-menu-item] {
    padding: 0.5rem 0.25rem;
  }

  & [data-reach-menu-item][data-selected] {
    @apply text-black bg-dove;
  }
`;

const ALERT_COLOR_STYLE = css`
  color: #666;
`;

const NotificationsMenu = ({ className }: any) => {
  const { notificationsList, containsNew } = useNotificationsState();
  const dispatch = useNotificationsDispatch();

  return (
    <Menu>
      {({ isExpanded }) => {
        useModalToggleTracking(isExpanded, { id: "notifications" });

        return (
          <>
            <MenuButton
              className={cn(className, "relative")}
              onClick={() => {
                try {
                  if (containsNew) {
                    notificationsViewed(dispatch);
                  }
                } catch (error) {}
              }}
            >
              <div className="absolute" style={{ right: "1px", top: "-4px" }}>
                <DotIcon
                  height={"0.5rem"}
                  width={"0.5rem"}
                  fill={containsNew ? colors.paloma : "transparent"}
                />
              </div>
              <NotificationIcon
                width="1.5rem"
                height="1.5rem"
                fill={colors.lapel}
              />
            </MenuButton>
            <MenuList className={ReachMenuListOverride}>
              {notificationsList.length > 0 ? (
                notificationsList.map(
                  ({
                    id,
                    link,
                    read_at,
                    notifier_name,
                    action,
                    parent_object_name,
                    direct_notified_at,
                  }) => {
                    const notifiedAt = distanceInWords(
                      Date.now(),
                      direct_notified_at,
                      {
                        addSuffix: true,
                      }
                    );

                    return (
                      <MenuLink
                        key={id}
                        className={`${NotificationClasses} flex flex-col`}
                        onSelect={() => {}}
                        as={AppLink}
                        href={link}
                        tracking={{ id: "notifications" }}
                      >
                        <span
                          className={cn(
                            "block w-full paragraph-xsmall text-lapel whitespace-normal",
                            { "font-light": read_at, "font-medium": !read_at }
                          )}
                        >
                          {notifier_name && (
                            <span className="font-bold">{notifier_name} </span>
                          )}
                          {action && <span>{action} </span>}
                          {parent_object_name && (
                            <span>{parent_object_name}</span>
                          )}
                        </span>
                        <span
                          className={`${ALERT_COLOR_STYLE} block paragraph-xsmall text-charcoal`}
                        >
                          <span
                            className={cn(
                              "paragraph-small text-lapel truncate",
                              {
                                "font-light": read_at,
                                "font-medium": !read_at,
                              }
                            )}
                          >
                            {`${notifier_name} ${action} ${parent_object_name}`}
                          </span>
                          <span className={`paragraph-xsmall text-charcoal`}>
                            {notifiedAt}
                          </span>
                        </span>
                      </MenuLink>
                    );
                  }
                )
              ) : (
                <MenuItem onSelect={() => {}}>
                  <div
                    className={`${NotificationClasses} paragraph-xsmall text-center px-3`}
                  >
                    You have no notifications.
                  </div>
                </MenuItem>
              )}
            </MenuList>
          </>
        );
      }}
    </Menu>
  );
};

export default NotificationsMenu;
